// no default React import needed with react-jsx
import { useState, useEffect } from 'react'
import { Box, Typography, Paper, TextField, Button, MenuItem, FormControl, InputLabel, Select } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'

// Register all community modules to enable features without individual imports
ModuleRegistry.registerModules([AllCommunityModule])
import { Search as SearchIcon, FilterList as FilterIcon } from '@mui/icons-material'
import { Track } from '../types/api'
import { adminApi, segmentsApi } from '../lib/sdkClient'

export default function TracksPage() {
  const [tracks, setTracks] = useState<Track[]>([])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchTerm, setSearchTerm] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('')
  const [trackTypeFilter, setTrackTypeFilter] = useState('')
  const [segmentCounts, setSegmentCounts] = useState<Record<number, number>>({})
  const navigate = useNavigate()

  const fetchTracks = async () => {
    try {
      const filters: Record<string, any> = {}
      if (visibilityFilter) filters.visibility = visibilityFilter
      if (trackTypeFilter) filters.track_type = trackTypeFilter

      const { data } = await adminApi.adminTracksArtrackAdminTracksGet(
        visibilityFilter || undefined,
        trackTypeFilter || undefined,
        pageSize,
        page * pageSize,
      )

      const items = (data as any)?.items || []
      setTracks(items)
      // Fetch segment counts in background
      Promise.all(
        items.map(async (t: any) => {
          try {
            const res = await segmentsApi.listSegmentsArtrackTracksTrackIdSegmentsGet(t.id as any)
            const count = Array.isArray(res.data) ? res.data.length : 0
            return [t.id, count] as [number, number]
          } catch {
            return [t.id, 0] as [number, number]
          }
        })
      ).then((pairs) => {
        const map: Record<number, number> = {}
        for (const [id, c] of pairs) map[id] = c
        setSegmentCounts(map)
      })
    } catch (error) {
      console.error('Error fetching tracks:', error)
    }
  }

  useEffect(() => {
    fetchTracks()
  }, [page, pageSize, searchTerm, visibilityFilter, trackTypeFilter])

  // Note: delete action removed in AG Grid migration

  const gridApiRef = useRef<any>(null)
  const columnDefs: any[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Track Name', flex: 2, minWidth: 360 },
    {
      headerName: 'Owner', flex: 1, minWidth: 200,
      valueGetter: (params: any) => params.data?.owner?.display_name || params.data?.owner_id || 'N/A',
    },
    { field: 'visibility', headerName: 'Visibility', flex: 0.6, minWidth: 140 },
    { field: 'track_type', headerName: 'Type', flex: 0.7, minWidth: 160 },
    {
      field: 'created_at', headerName: 'Created', flex: 0.8, minWidth: 160,
      valueGetter: (p: any) => new Date(p.data?.created_at).toLocaleDateString(),
    },
    {
      headerName: 'Waypoints', flex: 0.6, minWidth: 140,
      valueGetter: (p: any) => (p.data?.stats?.total_waypoints ?? p.data?.waypoint_count ?? 0),
    },
    {
      headerName: 'Segments', flex: 0.6, minWidth: 120,
      valueGetter: (p: any) => segmentCounts[p.data?.id] ?? '—',
    },
    {
      headerName: 'Collaborators', flex: 0.6, minWidth: 160,
      valueGetter: (p: any) => (p.data?.stats?.total_collaborators ?? p.data?.collaborator_count ?? 0),
    },
  ]
  const defaultColDef: any = { resizable: true, sortable: true, filter: true }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Track Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search tracks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
              label="Visibility"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Track Type</InputLabel>
            <Select
              value={trackTypeFilter}
              onChange={(e) => setTrackTypeFilter(e.target.value)}
              label="Track Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="hiking">Hiking</MenuItem>
              <MenuItem value="biking">Biking</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="driving">Driving</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => {
              setSearchTerm('')
              setVisibilityFilter('')
              setTrackTypeFilter('')
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* AG Grid */}
      <Paper sx={{ height: 'calc(100vh - 220px)', width: '100%', maxWidth: '100%' }}>
        <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
          <AgGridReact
            ref={gridApiRef}
            rowData={tracks as any[]}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination
            paginationPageSize={pageSize}
            onGridReady={(params) => { gridApiRef.current = params.api }}
            onPaginationChanged={() => {
              const api = gridApiRef.current
              if (!api) return
              const newPage = api.paginationGetCurrentPage()
              if (newPage !== page) setPage(newPage)
              const newSize = api.paginationGetPageSize()
              if (newSize !== pageSize) setPageSize(newSize)
            }}
            getRowId={(p) => String(p.data?.id)}
            suppressDragLeaveHidesColumns
            animateRows
            onRowClicked={(e) => {
              if (e?.data?.id) navigate(`/waypoints?trackId=${e.data.id}`)
            }}
          />
        </div>
      </Paper>
    </Box>
  )
}